// @ts-strict-ignore
import debounce from 'lodash/debounce';

/** Syncs multiple scroll boxes.
 * Limitations: scroll boxes must be the same size, only handles horizontal scrolling, not optimized
 * for lots of nodes (ie doesn't use IntersectionObserver API to prioritize visible scroll boxes).
 *
 * By default, this applies the class `scroll-follower` to scroll boxes that are being synced.
 * This can be disabled by passing {followerClass: null} to the constructor
 *
 * @example
 * const syncer = new ScrollSyncer();
 * syncer.register(document.querySelector(`#scroll-box`));
 * // sometime later-- drop single element reference and remove listeners:
 * syncer.deregister(element);
 * // drop all elements and remove listeners:
 * syncer.reset();
 */
export class ScrollSyncer {
  declare handleScroll;
  declare scheduleUnsetLeader;
  declare unsetLeaderDelay: number;
  declare leader?: HTMLElement;
  declare followerClass?: string;
  private scrollBoxes: HTMLElement[] = [];

  constructor({followerClass = `scroll-follower`, unsetLeaderDelay = 200} = {}) {
    this.followerClass = followerClass;
    this.unsetLeaderDelay = unsetLeaderDelay;
    this.handleScroll = (ev) => {
      if (this.leader === ev.target || !this.leader) {
        this.setLeader(ev.target);
        this.syncScroll(this.leader);
        this.scheduleUnsetLeader();
      }
    };

    this.scheduleUnsetLeader = debounce(() => {
      this.syncScroll(this.leader);
      this.leader = null;
    }, this.unsetLeaderDelay);
  }

  syncScroll(leader) {
    this.scrollBoxes.forEach((box) => {
      if (box !== leader) {
        box.scrollLeft = leader.scrollLeft;
      }
    });
  }

  /** Register an element to keep in sync */
  register(element: HTMLElement) {
    if (!element) {
      console.error(`ScrollSync#register Error: no element received`);
      return;
    }
    element.addEventListener(`scroll`, this.handleScroll);
    this.scrollBoxes.push(element);
  }

  /** Remove event listener and drop reference to an element */
  deregister(element: HTMLElement) {
    // note: does not remove followerClass from the element (this method is normally used when the element is about to be destroyed)
    const elementIndex = this.scrollBoxes.indexOf(element);
    if (elementIndex !== -1) {
      const item = this.scrollBoxes.splice(elementIndex, 1)[0];
      item.removeEventListener(`scroll`, this.handleScroll);
    }
  }

  /** Remove event listeners and drop all element references */
  reset() {
    this.scrollBoxes.forEach((box) => {
      box.removeEventListener(`scroll`, this.handleScroll);
    });
    this.scrollBoxes = [];
  }

  private setLeader(element) {
    if (this.leader !== element) {
      this.leader = element;
      if (this.followerClass) {
        this.updateFollowerClass(this.followerClass);
      }
    }
  }

  private updateFollowerClass(className) {
    this.scrollBoxes.forEach((box) => {
      box === this.leader ? box.classList.remove(className) : box.classList.add(className);
    });
  }
}
